import SnapshotTable, {type SnapshotDataRef} from "../components/data_table/SnapshotTable.tsx";
import {type FormEvent, useCallback, useEffect, useRef, useState} from "react";
import {swal_util} from "../lib/SwalUtil.ts";
import {Navbar} from "../components/Navbar.tsx";
import {snapshot as snapshotApi, SnapshotAPIConnectorError} from "../lib/Snapshot.ts";
import {Button, Form, Modal} from "react-bootstrap";
import {type Snapshot as SnapshotType} from "../lib/ModelTypes.ts";
import Loading from "./Loading.tsx";
import {useTranslation} from "react-i18next";

interface ContentProps {
    toggleSidebar: () => void;
}

const Snapshot = ({toggleSidebar}: ContentProps) => {
    const {t} = useTranslation();

    const snapshotRefreshInterval = 5000; // 스냅샷 자동 갱신 타입 ms 단위.
    const autoReloadInterval = useRef<ReturnType<typeof setTimeout> | null>(null);

    const dataTableRef = useRef<SnapshotDataRef>(null);
    const [isShowEditModal, setShowEditModal] = useState(false);
    const [isShowDeleteModal, setShowDeleteModal] = useState(false);
    const [modifyFormSnapshot, setModifyFormSnapshot] = useState<SnapshotType>({
        created: "",
        id: 0,
        name: "",
        os_image_name: "",
        server_id: 0,
        status: "",
        user_id: ""
    });

    async function onClickSnapshotMultipleDelete() {
        const selected = dataTableRef.current?.getSelectedData() || [];
        if (selected.length === 0) {
            await swal_util.show_warning(t("snapshot.noSnapshotSelected"));
            return;
        }

        const result = await swal_util.show_question(
            t("snapshot.snapshotDeleteTitle"),
            t("snapshot.deleteConfirm")
        );
        if (!result.isConfirmed) return;

        try {
            setShowDeleteModal(true);
            for (const snapshot of selected) {
                await snapshotApi.delete(snapshot.id);
                dataTableRef.current?.delete(snapshot);
            }

            await swal_util.show_success(t("snapshot.deleted"));
            setShowDeleteModal(false);
        } catch (err) {
            setShowDeleteModal(false);

            if (err instanceof SnapshotAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(t("snapshot.errorSnapshotDelete"));
            }
            console.error(err);
        }

    }

    async function onClickSnapshotDelete(snapshot: SnapshotType) {
        if (snapshot.id === 0) {
            await swal_util.show_warning(t("snapshot.noSnapshotSelected"))
            return;
        }
        const sweetAlertResult = await swal_util.show_question(t("snapshot.snapshotDeleteTitle"), `${snapshot.name} ${t("snapshot.snapshotDeleteQuestion")}`);
        if (!sweetAlertResult.isConfirmed) {
            await swal_util.show_warning(`${snapshot.name} ${t("snapshot.snapshotDeleteCancel")}`);
            return;
        }

        dataTableRef.current?.delete(snapshot);
        await snapshotApi.delete(snapshot.id);
    }

    const onSelectSnapshot = useCallback((snapshot: SnapshotType) => {
        setModifyFormSnapshot(snapshot);
        setShowEditModal(true);
    }, []);
    const onSubmitModifyDialog = useCallback((e: FormEvent) => {
        e.preventDefault();

        if (!(e.target instanceof HTMLFormElement)) {
            swal_util.show_error("e.target is not HTMLFormElement").finally();
            console.error("e.target is not HTMLFormElement");
            return;
        }
        const formData = new FormData(e.target);
        const snapshotId = parseInt(formData.get("id") as string);
        const snapshotName = formData.get("name") as string;

        if (modifyFormSnapshot.id === 0) {
            swal_util.show_warning(t("snapshot.noSnapshotSelected")).finally();
            return false;
        }
        snapshotApi.modify_name(snapshotId, snapshotName).then(async () => {
            await swal_util.show_success(t("common.updateSucess"));
            setShowEditModal(false);
        });

        dataTableRef.current?.modify({...modifyFormSnapshot, name: snapshotName});
    }, [modifyFormSnapshot, t]);

    const invalidate = useCallback(async () => {
        const snapshots = await snapshotApi.getSnapshotList();
        dataTableRef.current?.redraw(snapshots);
    }, []);

    useEffect(() => {
        if (dataTableRef.current) {
            invalidate().finally();
        }

        autoReloadInterval.current = setInterval(() => invalidate(), snapshotRefreshInterval);

        return () => {
            const intervalId = autoReloadInterval.current;
            if (!intervalId) return;

            clearInterval(intervalId);
        }
    }, [dataTableRef, invalidate]);

    return (
        <main className="d-block col ps-2 pt-3 min-vh-100">
            <Navbar toggleSidebar={toggleSidebar}/>
            {
                isShowDeleteModal && <Loading text={t("snapshot.snapshotDeleting")}/>
            }

            <article className="blueshift_main" id="wrapper">
                <div className="p-4 pt-2">
                    <div className="row">
                        <h6 className="text-primary pt-2 col-6">{t("title.snapshotManagement")}</h6>
                        <div className="text-end col-6">
                            <button type="button" className="button_red rounded-3 py-2"
                                    style={{fontSize: "14px"}} onClick={onClickSnapshotMultipleDelete}>
                                {t("snapshot.snapshotDeleteTitle")}
                            </button>
                        </div>
                    </div>
                    <div className="text-center align-items-center">
                        {/*<h5>데이터 불러오는 중</h5>*/}
                        <SnapshotTable
                            ref={dataTableRef}
                            onSelectSnapshot={onSelectSnapshot}
                            onClickDelete={onClickSnapshotDelete}
                        />
                    </div>
                </div>
                <Modal show={isShowEditModal} onHide={() => setShowEditModal(false)}>
                    <Modal.Header>
                        <Modal.Title><h6 className="text-primary pt-2">{t("snapshot.snapshotRenameTitle")}</h6></Modal.Title>
                        <Button variant="white" className="btn_close" onClick={() => setShowEditModal(false)}>X
                        </Button>
                    </Modal.Header>
                    <Modal.Body>
                        <Form className="modal-body" onSubmit={onSubmitModifyDialog}>
                            <input type="hidden" name="id" value={modifyFormSnapshot?.id || ""}/>
                            <input type="text"
                                   name="name"
                                   className="form-control bg-light px-4"
                                   placeholder={t("snapshot.snapshotnamePlaceholder")}
                                   defaultValue={modifyFormSnapshot?.name || ""}
                                   style={{height: "45px", fontSize: "15px", border: "none"}}/>
                            <Button variant="primary" className="mt-3" style={{float: "right"}} type="submit">
                                저장
                            </Button>
                        </Form>
                    </Modal.Body>
                </Modal>

            </article>
        </main>
    );
}

export default Snapshot;